import { Box, Checkbox, Chip, Typography } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"
import type { InputHTMLAttributes } from "react"

import { ProductStatusCell } from "@/components/table/ProductStatusCell"
import type { Product } from "@/graphql/codegen/graphql"
import { PRIORITY_STATUS_CLASS_MAP, PRIORITY_STATUS_LABELS } from "@/screens/CustomerProducts/constants"
import type { CompanyProductAssociationListItem } from "@/screens/CustomerProducts/stores/useCustomerProductAssociationsStore"
import { PRODUCT_STOCK_TYPE_CHIP_COLOR, PRODUCT_STOCK_TYPE_LABEL_MAP } from "@/screens/Products/constants"

export const useApprovedProductColumns = (): ColumnDef<CompanyProductAssociationListItem>[] => [
  {
    id: "select",
    header: "",
    enableSorting: false,
    size: 50,
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        disabled={!row.getCanSelect()}
        inputProps={
          {
            "data-testid": `customer-orders.approved-products-table.select-checkbox-${row.original.productId}`,
          } as InputHTMLAttributes<HTMLInputElement>
        }
      />
    ),
  },
  {
    accessorKey: "productId",
    header: "Product ID",
    enableSorting: false,
    cell: ({ row }) => {
      const product = row.original.product
      return product ? <ProductStatusCell product={product as unknown as Product} /> : null
    },
  },
  {
    accessorKey: "customerDescription",
    header: "Description",
    enableSorting: false,
    cell: ({ row }) => (
      <Box className='flex flex-col'>
        <Typography variant='body2' className='font-medium text-gray-800'>
          {row.original.customerDescription ?? row.original.product?.companyProductDescription ?? null}
        </Typography>
        {row.original.product?.companyProductDescription && (
          <Typography variant='body2' className='text-gray-600'>
            {row.original.product.companyProductDescription}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    accessorKey: "locationCustomerCode",
    header: "Customer Code",
    enableSorting: false,
    cell: ({ row }) => {
      const codes = row.original.locationCustomerCode ?? []
      if (!codes.length) return null
      return (
        <Box className='flex flex-wrap gap-1'>
          {codes.map((code) => (
            <Chip key={code} size='small' label={code} className='rounded-[4px] bg-gray-300 text-xs' />
          ))}
        </Box>
      )
    },
  },
  {
    id: "supplier",
    accessorFn: (row) => row.product?.vendorDescription ?? "",
    header: "Supplier",
    enableSorting: false,
    cell: ({ row }) => row.original.product?.vendorDescription ?? null,
  },
  {
    id: "priority",
    accessorFn: (row) => row.priorityStatus ?? "",
    header: "Priority",
    enableSorting: false,
    cell: ({ row }) => {
      const status = row.original.priorityStatus
      if (!status) return null
      return (
        <Chip
          size='small'
          label={PRIORITY_STATUS_LABELS[status]}
          className={`rounded-[2px] text-xs font-normal ${PRIORITY_STATUS_CLASS_MAP[status]}`}
        />
      )
    },
  },
  {
    id: "stockType",
    accessorFn: (row) => row.product?.stockType ?? "",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => {
      const stockType = row.original.product?.stockType
      if (!stockType) return null
      return (
        <Chip
          size='small'
          label={PRODUCT_STOCK_TYPE_LABEL_MAP[stockType]}
          color={PRODUCT_STOCK_TYPE_CHIP_COLOR[stockType]}
        />
      )
    },
  },
  {
    id: "lastOrdered",
    header: "Last Ordered",
    enableSorting: false,
    // TODO(AK-6130): No data source exists for last-ordered info on CompanyProductAssociationLocationItem.
    cell: () => null,
  },
]
